import React, { useCallback } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  LinearProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Theme,
  Typography,
} from '@material-ui/core';
import { MenuHeaderTemplate } from '../../components/templates/MenuHeaderTemplate';
import { BasicTableHead, BasicTableHeadProps } from '../../components/molecules';
import { FormProvider, useForm } from 'react-hook-form';
import { Launch, Search } from '@material-ui/icons';
import { useUsageLogUseCase } from './hooks/usage-log-use-case';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useLocation } from 'react-router';
import { InvokeCouponUsageLogJsonQueryVariables } from '../../graphql/generated';
import { Link as RouterLink } from 'react-router-dom';
import { formatJstDateTimeJapanese } from '../../util';
import { ReadMoreButton } from '../../components/organisms/general/ReadMoreButton';
import { useUrlSearchParamOrganization } from '../../components/contexts/hooks/url';
import { TextFieldController } from '../../components/organisms/form/controllers/TextFieldController';
import { CheckBoxController } from '../../components/organisms/form/controllers/CheckBoxController';

const useStyles = makeStyles((theme: Theme) => ({
  area: {
    marginTop: theme.spacing(3),
  },
  section: {
    marginTop: theme.spacing(1),
  },
}));

export type CouponUsageLogFormValues = InvokeCouponUsageLogJsonQueryVariables;

export const CouponUsageLog: React.FC = () => {
  const theme = useTheme();
  const classes = useStyles();
  const state = useLocation<{ name: string }>().state;
  const organizationParam = useUrlSearchParamOrganization();
  const form = useForm<CouponUsageLogFormValues>();
  const { handleSubmit } = form;
  const {
    data,
    loading,
    csvLoading,
    onSearch,
    onFetchMore,
    onCsvOut,
    organization,
    organizationLoading,
    userInfo,
    allAcquired,
  } = useUsageLogUseCase();

  const usageTableHeadProps: BasicTableHeadProps = {
    columns: [
      {
        name: 'organizationName',
        label: '組織名',
        minWidth: theme.spacing(10),
        align: 'left',
      },
      {
        name: 'couponCode',
        label: 'クーポンコード / 名',
        minWidth: theme.spacing(10),
        align: 'left',
      },
      {
        name: 'barcode',
        label: 'バーコード',
        minWidth: theme.spacing(10),
        align: 'left',
      },
      {
        name: 'url',
        label: 'URL',
        minWidth: theme.spacing(10),
        align: 'left',
      },
      {
        name: 'createAtMillis',
        label: '最終更新日時',
        minWidth: theme.spacing(10),
        align: 'left',
      },
      {
        name: 'status',
        label: 'ステータス',
        minWidth: theme.spacing(10),
        align: 'left',
      },
      {
        name: 'usedShopName',
        label: '利用店舗',
        minWidth: theme.spacing(10),
        align: 'left',
      },
      {
        name: 'editButton',
        label: 'クーポン詳細',
        minWidth: theme.spacing(10),
        align: 'center',
        disabled: !userInfo?.permissions.includes('show:coupon-detail'),
      },
    ],
  };

  // テーブルボディを切り替える
  const tableBody = useCallback(() => {
    if (!data || !data.pollCouponUsageLogJson) {
      return;
    } else if (!data.pollCouponUsageLogJson.nodes || data.pollCouponUsageLogJson.nodes.length === 0) {
      return (
        <TableRow>
          <TableCell>データがありません</TableCell>
        </TableRow>
      );
    } else {
      return data?.pollCouponUsageLogJson.nodes.map((log) => (
        <TableRow key={log.couponId}>
          <TableCell>{log.organizationName}</TableCell>
          <TableCell>
            {log.couponCode} / {log.couponName}
          </TableCell>
          <TableCell>{log.barcode}</TableCell>
          <TableCell>{log.url}</TableCell>
          <TableCell>{formatJstDateTimeJapanese(log.createAtMillis)}</TableCell>
          <TableCell>{log.status}</TableCell>
          <TableCell>{log.usedShopName}</TableCell>
          {userInfo?.permissions.includes('show:coupon-detail') ? (
            <TableCell align="center" scope="row">
              <IconButton
                target="_blank"
                href={`/coupon-masters/${log.couponMasterId}/coupons/${log.couponId}?${organizationParam}`}
              >
                <Launch fontSize="small" color={'inherit'} />
              </IconButton>
            </TableCell>
          ) : (
            <></>
          )}
        </TableRow>
      ));
    }
  }, [data]);

  // 組織情報が取得できていないタイミングでは、ローディングを出す
  if (organizationLoading || !organization) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <MenuHeaderTemplate header={{ title: 'クーポン利用状況一覧' }}>
        <Container maxWidth="xl">
          {state?.name ? (
            <Box className={classes.section}>
              <Breadcrumbs>
                <Link
                  color="inherit"
                  component={RouterLink}
                  to={{
                    pathname: `/coupon-usage`,
                    search: organizationParam,
                  }}
                >
                  クーポン利用状況集計
                </Link>
                <Box>
                  <Typography color="textPrimary">{state.name}</Typography>
                </Box>
              </Breadcrumbs>
            </Box>
          ) : (
            <></>
          )}
          <Typography variant="caption" className={classes.section}>
            【一覧の並び順】クーポンコード,クーポン名,バーコード,利用日時,ステータス,利用店舗
          </Typography>
          <Box className={classes.area}>
            <FormProvider {...form}>
              <form onSubmit={handleSubmit(onSearch)} autoComplete="off" noValidate>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextFieldController
                      name="couponCode"
                      label="クーポンコード"
                      disabled={loading || csvLoading}
                      size="small"
                      rule={{
                        maxLength: {
                          value: 30,
                          message: '30文字以内で入力してください',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextFieldController
                      name="couponName"
                      label="クーポン名"
                      size="small"
                      disabled={loading || csvLoading}
                      rule={{
                        maxLength: {
                          value: 30,
                          message: '30文字以内で入力してください',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextFieldController
                      name="organizationName"
                      label="組織名"
                      size="small"
                      disabled={loading || csvLoading}
                      rule={{
                        maxLength: {
                          value: 30,
                          message: '30文字以内で入力してください',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextFieldController
                      name="barcode"
                      label="バーコード"
                      size="small"
                      disabled={loading || csvLoading}
                      rule={{
                        maxLength: {
                          value: 30,
                          message: '30文字以内で入力してください',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextFieldController
                      name="url"
                      label="URL"
                      size="small"
                      disabled={loading || csvLoading}
                      rule={{
                        maxLength: {
                          value: 100,
                          message: '100文字以内で入力してください',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <CheckBoxController name="isIssued" label="発行済み" disabled={loading || csvLoading} />
                    <CheckBoxController name="isUsed" label="使用済み" disabled={loading || csvLoading} />
                    <CheckBoxController name="isCanceled" label="キャンセル" disabled={loading || csvLoading} />
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={2}>
                        <Button
                          type="submit"
                          variant="outlined"
                          size="small"
                          color={'default'}
                          disabled={loading || csvLoading}
                          endIcon={<Search />}
                          fullWidth
                        >
                          を含む
                        </Button>
                      </Grid>
                      <Grid item xs={2}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="small"
                          color={'secondary'}
                          disabled={loading || csvLoading}
                          onClick={handleSubmit(onCsvOut)}
                          fullWidth
                        >
                          {csvLoading ? <CircularProgress size={30} /> : 'CSV出力'}
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </form>
            </FormProvider>
          </Box>

          <Grid container spacing={2} className={classes.area}>
            <Grid item xs={12}>
              <TableContainer component={Paper}>
                {loading ? <LinearProgress /> : <React.Fragment />}
                <Table size={'small'}>
                  <BasicTableHead columns={usageTableHeadProps.columns} />
                  <TableBody>{tableBody()}</TableBody>
                </Table>
              </TableContainer>
            </Grid>
            {data?.pollCouponUsageLogJson?.nodes && !allAcquired ? (
              <ReadMoreButton fetchMore={handleSubmit(onFetchMore)} loading={loading} />
            ) : (
              <></>
            )}
          </Grid>
        </Container>
      </MenuHeaderTemplate>
    </>
  );
};
